// react
import styled, { css } from 'styled-components';

// components
import { Button, Card, Container } from '@/components/atoms';

interface InstructionProps {
  className?: string;
  children?: React.ReactNode;
  title?: string;
  subTitle?: string;
  instruction?: string;
  linkUrl?: string;
  linkText?: string;
}

/**
 * Instruction renders an instruction to the user.
 */
export const Instruction = ({
  className,
  children,
  title,
  subTitle,
  instruction,
  linkUrl,
  linkText,
}: InstructionProps) => {
  return (
    <StyledContainer size='md'>
      <Wrapper className={className}>
        <Header>
          {title && <Title>{title}</Title>}
          {subTitle && <SubTitle>{subTitle}</SubTitle>}
        </Header>
        {children}
        <Card color='green'>
          <StyledText>{instruction}</StyledText>
        </Card>
        {linkUrl && linkText && (
          <Button ariaLabel={linkText || ''} variant='link' to={linkUrl}>
            {linkText}
          </Button>
        )}
      </Wrapper>
    </StyledContainer>
  );
};

const StyledContainer = styled(Container)`
  overflow-y: auto;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => props.theme.spacing[24]};
  padding: ${(props) => props.theme.spacing[32]} 0;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: ${(props) => props.theme.spacing[24]};
`;

const Title = styled.div`
  font: ${(props) => props.theme.typography.heading['4xl']};
  color: ${(props) => props.theme.color.primary[80]};
  text-align: center;
  ${(props) =>
    props.theme.breakpoint.md(css`
      font: ${props.theme.typography.heading['6xl']};
      text-align: left;
    `)};
`;

const SubTitle = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[90]};
  text-align: center;

  ${(props) =>
    props.theme.breakpoint.md(css`
      font: ${props.theme.typography.body.xl.regular};
    `)}
`;

const StyledText = styled.div`
  font: ${(props) => props.theme.typography.heading['3xl']};
  color: ${(props) => props.theme.color.neutral[90]};
  text-align: center;
`;

Instruction.displayName = 'Instruction';

export default Instruction;
